import { ComponentProps, FC } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { ModalActionsData } from '../../store/store.types';
import Button from '../Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui';

type DialogModalProps = ComponentProps<typeof Dialog> & {
  dataTestId?: string;
  isOpen: boolean;
  title: string;
  modalActions: Record<string, ModalActionsData>;
  description?: string;
  customDescriptionContent?: React.ReactNode;
};

const DialogModal: FC<DialogModalProps> = ({
  dataTestId,
  isOpen,
  title,
  description,
  customDescriptionContent,
  modalActions,
  children,
  ...rest
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={modalActions.onClose.action} {...rest}>
      <div className="fixed inset-0 bg-white/50 backdrop-blur-sm z-[9999] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogContent
        className="bg-white p-6 z-[10000] max-w-[450px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-4"
        data-testid={dataTestId}
      >
        <DialogHeader className="relative">
          <DialogTitle className="text-p-l font-semibold">{title}</DialogTitle>
          {(customDescriptionContent || description) && (
            <DialogDescription className="text-p-s text-base-mutedForeground">
              {customDescriptionContent || description}
            </DialogDescription>
          )}
          <Button
            className="absolute top-[-16px] right-[-16px] h-4 w-4"
            onClick={modalActions.onClose.action}
            rightIcon={<FontAwesomeIcon icon="fa-regular fa-xmark" />}
            variant="ghost"
          />
        </DialogHeader>
        {children && <div>{children}</div>}
        <DialogFooter className="gap-2">
          {Object.entries(modalActions)
            .reverse()
            .map(([label, data]) => (
              <Button
                data-testid={`${dataTestId}-${label}`}
                key={label}
                onClick={data.action}
                variant={data.variant ?? 'default'}
                disabled={modalActions.onConfirm?.disabled ?? false}
                type="button"
              >
                {data.label}
              </Button>
            ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogModal;
